import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Instagram, Facebook, Twitter, Clock } from 'lucide-react';
import Header from './components/Header4';
import Footer from './components/Footer4';
import Button from './components/Button';

import { devLog } from '../../../utils/devLog';
const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
    });
    const [submitted, setSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        devLog('Contact form submitted:', formData);
        setSubmitted(true);
    };

    const contactInfo = [
        { icon: Phone, label: 'Phone', value: '+91 98765 43210', href: 'tel:+919876543210' },
        { icon: Mail, label: 'Email', value: 'hello@neela.in', href: 'mailto:hello@neela.in' },
        { icon: MapPin, label: 'Studio', value: '42 Marine Drive, Mumbai, Maharashtra 400001' },
        { icon: Clock, label: 'Hours', value: 'Mon-Sat: 10AM - 7PM IST' },
    ];

    const inputClass = "w-full px-4 py-2.5 text-sm border border-[#E5E7EB] rounded-sm focus:outline-none focus:border-[#1B4965] text-[#1A1A1A] bg-white";

    return (
        <div className="w-full min-h-screen bg-white" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            <Header />
            <link href="https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <main className="pt-20 md:pt-24">
                {/* Hero */}
                <div className="bg-[#1B4965] py-12 md:py-20 px-4 text-center">
                    <p className="text-xs tracking-[0.2em] uppercase mb-2 text-[#62B6CB] font-medium">Get in Touch</p>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-3">Contact Us</h1>
                    <p className="text-sm md:text-base text-white/70 max-w-lg mx-auto font-light">
                        We'd love to hear from you. Reach out for orders, collaborations, or any questions.
                    </p>
                </div>

                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
                        {/* Contact Info */}
                        <div className="lg:col-span-2">
                            <h2 className="text-xl font-semibold text-[#1B4965] mb-6">Reach Out</h2>
                            <div className="space-y-6">
                                {contactInfo.map((item, i) => {
                                    const Icon = item.icon;
                                    return (
                                        <div key={i} className="flex gap-4">
                                            <div className="w-10 h-10 bg-[#F0F7FA] rounded-sm flex items-center justify-center flex-shrink-0">
                                                <Icon className="w-5 h-5 text-[#1B4965]" />
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">{item.label}</p>
                                                {item.href ? (
                                                    <a href={item.href} className="text-sm text-[#1A1A1A] hover:text-[#1B4965] transition-colors">{item.value}</a>
                                                ) : (
                                                    <p className="text-sm text-[#1A1A1A]">{item.value}</p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Social */}
                            <div className="mt-8">
                                <p className="text-xs font-medium text-[#6B7280] uppercase tracking-wider mb-3">Follow Us</p>
                                <div className="flex gap-3">
                                    <a href="#" className="w-9 h-9 rounded-full bg-[#F0F7FA] hover:bg-[#1B4965] hover:text-white flex items-center justify-center text-[#1B4965] transition-all">
                                        <Instagram size={16} />
                                    </a>
                                    <a href="#" className="w-9 h-9 rounded-full bg-[#F0F7FA] hover:bg-[#1B4965] hover:text-white flex items-center justify-center text-[#1B4965] transition-all">
                                        <Facebook size={16} />
                                    </a>
                                    <a href="#" className="w-9 h-9 rounded-full bg-[#F0F7FA] hover:bg-[#1B4965] hover:text-white flex items-center justify-center text-[#1B4965] transition-all">
                                        <Twitter size={16} />
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Form */}
                        <div className="lg:col-span-3">
                            {submitted ? (
                                <div className="bg-[#F0F7FA] p-8 rounded-sm text-center">
                                    <div className="w-16 h-16 bg-[#1B4965] rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Send className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-[#1B4965] mb-2">Message Sent!</h3>
                                    <p className="text-sm text-[#6B7280]">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="bg-[#F0F7FA] p-6 sm:p-8 rounded-sm">
                                    <h2 className="text-lg font-semibold text-[#1B4965] mb-6">Send a Message</h2>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-[#6B7280] mb-1.5">Name</label>
                                            <input name="name" value={formData.name} onChange={handleInputChange} className={inputClass} placeholder="Your name" required />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-[#6B7280] mb-1.5">Email</label>
                                            <input name="email" type="email" value={formData.email} onChange={handleInputChange} className={inputClass} placeholder="you@email.com" required />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-[#6B7280] mb-1.5">Phone</label>
                                            <input name="phone" value={formData.phone} onChange={handleInputChange} className={inputClass} placeholder="+91 98765 43210" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-[#6B7280] mb-1.5">Subject</label>
                                            <input name="subject" value={formData.subject} onChange={handleInputChange} className={inputClass} placeholder="What's this about?" required />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label className="block text-xs font-medium text-[#6B7280] mb-1.5">Message</label>
                                            <textarea
                                                name="message"
                                                value={formData.message}
                                                onChange={handleInputChange}
                                                rows={5}
                                                className={`${inputClass} resize-none`}
                                                placeholder="Tell us more..."
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-6">
                                        <Button type="submit" variant="primary" size="lg" className="gap-2">
                                            <Send className="w-4 h-4" /> Send Message
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default Contact;
